import type {
  ApoyoVista,
  CapturaFila,
  CopiaMarcas,
  CopiaPropuesta,
  EstadoTienda,
  FilaVista,
  MotivoSinMeta,
  TiendaSemana,
} from '../types/metas.types';
import { esFilaCasa } from './casa';
import { pesosEnteros } from './formato';

/**
 * Edición local de una tienda: lo que el supervisor cambió y todavía no guardó.
 *
 * La pantalla guarda una copia de las filas que mandó el servidor y la edita;
 * al guardar manda TODAS las filas con la `version` que vio. Tras guardar, la
 * respuesta del servidor REEMPLAZA la copia: la identidad, los avisos y los
 * totales oficiales siempre son los del servidor.
 */
export interface ValoresFila {
  metaMonto: number | null;
  sinMetaMotivo: MotivoSinMeta | null;
  sinMetaDetalle: string | null;
  apoyo: { chain: string; num: number; dias: number } | null;
  /** Por persona desde el 21-oct-2026 (decisión del usuario). Sin el dato de la API: `false` / `null`. */
  transporte: boolean;
  bodegueroFijo: boolean;
  diasIncapacidad: number | null;
}

/**
 * La vista trae las marcas por persona (transporte, bodeguero fijo, días de
 * incapacidad): la API es la de la fase B (21-oct-2026). Sin ellas la pantalla
 * no ofrece las casillas ni las manda en el `PUT` (una API anterior, con
 * `forbidNonWhitelisted`, respondería 400).
 */
export const conMarcasPorPersona = (f: Pick<FilaVista, 'transporte'>): boolean => f.transporte !== undefined;

/** Los días de incapacidad que se eligen (la API acepta de 0 a 7; «—» = sin días). */
export const DIAS_INCAPACIDAD = [1, 2, 3, 4, 5, 6, 7] as const;

/**
 * Lo que la persona lleva por persona, en palabras («Transporte», «Bodeguero
 * fijo», «Incapacidad 3 d»), para el detalle del regional (21-oct-2026).
 */
export function marcasEnPalabras(f: Pick<FilaVista, 'transporte' | 'bodegueroFijo' | 'diasIncapacidad'>): string[] {
  return [
    f.transporte ? 'Transporte' : null,
    f.bodegueroFijo ? 'Bodeguero fijo' : null,
    f.diasIncapacidad ? `Incapacidad ${f.diasIncapacidad} d` : null,
  ].filter((x): x is string => !!x);
}

export function valoresDe(f: FilaVista): ValoresFila {
  return {
    metaMonto: f.metaMonto ?? null,
    sinMetaMotivo: f.sinMetaMotivo ?? null,
    sinMetaDetalle: f.sinMetaMotivo ? (f.sinMetaDetalle ?? '').trim() || null : null,
    apoyo: f.apoyo ? { chain: f.apoyo.chain, num: f.apoyo.num, dias: f.apoyo.dias } : null,
    transporte: !!f.transporte,
    bodegueroFijo: !!f.bodegueroFijo,
    diasIncapacidad: f.diasIncapacidad ?? null,
  };
}

export function mismosValores(a: ValoresFila, b: ValoresFila): boolean {
  return (
    a.metaMonto === b.metaMonto &&
    a.sinMetaMotivo === b.sinMetaMotivo &&
    a.sinMetaDetalle === b.sinMetaDetalle &&
    a.transporte === b.transporte &&
    a.bodegueroFijo === b.bodegueroFijo &&
    a.diasIncapacidad === b.diasIncapacidad &&
    (a.apoyo === null) === (b.apoyo === null) &&
    (a.apoyo === null ||
      (a.apoyo.chain === b.apoyo?.chain && a.apoyo.num === b.apoyo?.num && a.apoyo.dias === b.apoyo?.dias))
  );
}

/**
 * ¿Hay algo que guardar? Filas editadas o la nota del supervisor. (Las
 * personas agregadas desde «Vendieron aquí» ya no existen: la sección salió de
 * la pantalla el 21-oct-2026, decisión del usuario.) Desde la ronda F
 * (23-oct-2026) también cuenta agregar o quitar el renglón de la Casa: una fila
 * que no estaba o una que ya no está.
 */
export function hayCambios(
  original: FilaVista[],
  actual: FilaVista[],
  notaOriginal: string | null,
  notaActual: string | null,
): boolean {
  if ((notaOriginal ?? '').trim() !== (notaActual ?? '').trim()) return true;
  const antes = new Map(original.map((f) => [f.key, valoresDe(f)]));
  for (const f of actual) {
    const a = antes.get(f.key);
    if (!a || !mismosValores(a, valoresDe(f))) return true;
  }
  const ahora = new Set(actual.map((f) => f.key));
  return original.some((f) => !ahora.has(f.key));
}

/**
 * La Casa que se agregó o se quitó en pantalla y el servidor todavía no tiene
 * (ronda F): `agregar` y `quitar` del `PUT`. Se calcula contra lo guardado
 * (las filas de la última vista adoptada), así que se sigue mandando hasta que
 * la respuesta lo refleje.
 */
export function cambiosDeCasa(guardadas: { key: string; esCasa?: boolean }[], enPantalla: { key: string; esCasa?: boolean }[]) {
  const antes = new Set(guardadas.map((f) => f.key));
  const ahora = new Set(enPantalla.map((f) => f.key));
  return {
    agregar: enPantalla.filter((f) => esFilaCasa(f) && !antes.has(f.key)).map((f) => f.key),
    quitar: guardadas.filter((f) => esFilaCasa(f) && !ahora.has(f.key)).map((f) => f.key),
  };
}

/**
 * Cambia UN campo de una fila. Meta y motivo se excluyen: escribir una meta
 * quita el motivo, y elegir un motivo quita la meta. Así el supervisor nunca
 * llega al error «meta y motivo a la vez» por el orden en que llenó las
 * casillas; el error sigue existiendo para lo que venga de otro lado.
 */
export type CambioFila =
  | { campo: 'metaMonto'; valor: number | null }
  | { campo: 'sinMetaMotivo'; valor: MotivoSinMeta | null }
  | { campo: 'sinMetaDetalle'; valor: string }
  | { campo: 'apoyo'; valor: ApoyoVista | null }
  // Por persona (21-oct-2026): no tocan meta ni motivo. Los días de incapacidad
  // SÓLO informan (decisión del usuario): la meta la reduce quien captura.
  | { campo: 'transporte'; valor: boolean }
  | { campo: 'bodegueroFijo'; valor: boolean }
  | { campo: 'diasIncapacidad'; valor: number | null };

export function aplicarCambio(f: FilaVista, c: CambioFila): FilaVista {
  switch (c.campo) {
    case 'metaMonto':
      return {
        ...f,
        metaMonto: c.valor,
        ...(c.valor !== null ? { sinMetaMotivo: null, sinMetaDetalle: null } : {}),
      };
    case 'sinMetaMotivo':
      return {
        ...f,
        sinMetaMotivo: c.valor,
        sinMetaDetalle: c.valor === 'otro' ? f.sinMetaDetalle : null,
        ...(c.valor !== null ? { metaMonto: null } : {}),
      };
    case 'sinMetaDetalle':
      return { ...f, sinMetaDetalle: c.valor };
    case 'apoyo':
      return { ...f, apoyo: c.valor };
    case 'transporte':
      return { ...f, transporte: c.valor };
    case 'bodegueroFijo':
      return { ...f, bodegueroFijo: c.valor };
    case 'diasIncapacidad':
      return { ...f, diasIncapacidad: c.valor };
  }
}

/**
 * Lo que va en el `PUT`: todas las filas, con las llaves que dio el servidor.
 * Las marcas por persona sólo si la vista las trae (`conMarcasPorPersona`).
 * La Casa (ronda F) sólo lleva su meta: sin motivo, apoyo ni marcas.
 */
export function aCapturaFilas(filas: FilaVista[]): CapturaFila[] {
  return filas.map((f) => {
    if (esFilaCasa(f)) return { key: f.key, metaMonto: f.metaMonto ?? null, sinMetaMotivo: null, sinMetaDetalle: null, apoyo: null };
    const v = valoresDe(f);
    return {
      key: f.key,
      metaMonto: v.metaMonto,
      sinMetaMotivo: v.sinMetaMotivo,
      sinMetaDetalle: v.sinMetaDetalle,
      apoyo: v.apoyo,
      ...(conMarcasPorPersona(f)
        ? { transporte: v.transporte, bodegueroFijo: v.bodegueroFijo, diasIncapacidad: v.diasIncapacidad }
        : {}),
    };
  });
}

/**
 * «El gerente puso $X» (21-oct-2026, decisión del usuario: los ajustes del
 * supervisor se ven). EN VIVO, contra la meta que el gerente dejó al enviarla
 * (`metaGerente`), sólo si la tienda pasó por él (`conGerente`: se la mandó y
 * no está otra vez en sus manos). `null` = no hay ajuste que decir.
 */
export function ajusteDelGerente(f: Pick<FilaVista, 'metaMonto' | 'metaGerente'>, conGerente: boolean): string | null {
  if (!conGerente || f.metaGerente === undefined) return null;
  const g = f.metaGerente ?? null;
  const m = f.metaMonto ?? null;
  if (g === m) return null;
  return g === null ? 'El gerente no le puso meta' : `El gerente puso ${pesosEnteros(g)}`;
}

/**
 * ¿La tienda pasó por el gerente y está después de él? (La API sólo manda
 * `metaGerente` así: en borrador o sin captura la está editando él mismo.)
 */
export const pasoPorGerente = (e: { estado: EstadoTienda | string; enviadoGerenteEn?: string | null }): boolean =>
  !!e.enviadoGerenteEn && e.estado !== 'borrador' && e.estado !== 'sin_captura';

/** Tiene meta o motivo escritos (en la copia local, guardados o no). */
export const tieneCaptura = (f: { metaMonto: number | null | undefined; sinMetaMotivo: unknown }): boolean =>
  (f.metaMonto !== null && f.metaMonto !== undefined) || !!f.sinMetaMotivo;

/** Una propuesta de «Copiar semana anterior» que no se pudo poner en ninguna fila, y por qué. */
export interface CopiaSaltada {
  key: string;
  nombre: string;
  mensaje: string;
}

/**
 * Las propuestas que PISARÍAN algo escrito: su fila ya tiene meta o motivo (en
 * pantalla, guardado o no) y lo propuesto es distinto. Con una sola, la
 * pantalla pregunta antes de copiar (SPEC: «pide confirmación si ya hay datos»).
 */
export function propuestasQuePisan(filas: FilaVista[], propuestas: CopiaPropuesta[]): CopiaPropuesta[] {
  const porKey = new Map(filas.map((f) => [f.key, f]));
  return propuestas.filter((p) => {
    const f = porKey.get(p.key);
    if (!f || !tieneCaptura(f)) return false;
    // El detalle sólo cuenta con «Otro»: `aplicarCopia` lo descarta con los
    // demás motivos, así que compararlo anunciaba un conflicto que, al
    // reemplazar, dejaba la fila igual.
    return !(
      (f.metaMonto ?? null) === (p.metaMonto ?? null) &&
      (f.sinMetaMotivo ?? null) === (p.sinMetaMotivo ?? null) &&
      (p.sinMetaMotivo !== 'otro' ||
        ((f.sinMetaDetalle ?? '').trim() || null) === ((p.sinMetaDetalle ?? '').trim() || null))
    );
  });
}

/**
 * Aplica la propuesta de la API a la copia LOCAL (no guarda: se guarda como
 * cualquier cambio, con el autoguardado o «Guardar borrador»).
 *
 * - `reemplazar: false` sólo llena las filas sin meta ni motivo; nunca pisa lo
 *   escrito. `true` pone lo de la semana anterior también donde ya había algo
 *   (sólo después de que la persona lo confirmó).
 * - Meta O motivo, como la API: poner uno quita el otro.
 * - El apoyo sólo viene con «Apoyo en otra tienda»; en lo demás se conserva el
 *   de la fila (el apoyo por días no se copia).
 * - Un puesto que no lleva meta no recibe una meta, aunque la propuesta la
 *   traiga (la API ya no la manda; esto es por si acaso).
 */
export function aplicarCopia(
  filas: FilaVista[],
  propuestas: CopiaPropuesta[],
  opciones: {
    reemplazar: boolean;
    catalogo?: { chain: string; num: number; tienda: string }[];
    /** Transporte y Bodeguero fijo de la semana anterior (21-oct-2026; `marcas` de la API). */
    marcas?: CopiaMarcas[];
  },
): { filas: FilaVista[]; copiadas: number; respetadas: number; saltadas: CopiaSaltada[]; marcadas: number } {
  const porKey = new Map(propuestas.map((p) => [p.key, p]));
  const conocidas = new Set(filas.map((f) => f.key));
  const saltadas: CopiaSaltada[] = propuestas
    .filter((p) => !conocidas.has(p.key))
    .map((p) => ({ key: p.key, nombre: p.nombre, mensaje: 'no está en la pantalla: recarga la tienda' }));
  let copiadas = 0;
  let respetadas = 0;
  const nombreTienda = (chain: string, num: number) =>
    opciones.catalogo?.find((t) => t.chain === chain && t.num === num)?.tienda ?? null;
  const conMeta = filas.map((f) => {
    const p = porKey.get(f.key);
    if (!p) return f;
    // La meta de la Casa no se copia (ronda F): la API ya no la propone; esto es por si acaso.
    if (esFilaCasa(f)) {
      saltadas.push({ key: f.key, nombre: f.nombre, mensaje: 'la meta de la Casa no se copia: ponla cada semana que juegue' });
      return f;
    }
    if (tieneCaptura(f) && !opciones.reemplazar) {
      respetadas++;
      return f;
    }
    if (p.metaMonto !== null && p.metaMonto !== undefined) {
      if (f.reglaMeta === 'no_lleva') {
        saltadas.push({ key: f.key, nombre: f.nombre, mensaje: 'su puesto no lleva meta individual' });
        return f;
      }
      copiadas++;
      return { ...f, metaMonto: p.metaMonto, sinMetaMotivo: null, sinMetaDetalle: null };
    }
    if (!p.sinMetaMotivo) return f;
    copiadas++;
    return {
      ...f,
      metaMonto: null,
      sinMetaMotivo: p.sinMetaMotivo,
      sinMetaDetalle: p.sinMetaMotivo === 'otro' ? p.sinMetaDetalle : null,
      apoyo: p.apoyo ? { ...p.apoyo, tienda: nombreTienda(p.apoyo.chain, p.apoyo.num) } : f.apoyo,
    };
  });
  // Las marcas por persona (21-oct-2026) van aparte: valen también para quien
  // no lleva meta. «Sólo llenar los vacíos» no pisa una persona que ya tiene
  // alguna marcada esta semana; «Bodeguero fijo» sólo a un puesto de bodega.
  const marcas = new Map((opciones.marcas ?? []).map((m) => [m.key, m]));
  let marcadas = 0;
  const out = conMeta.map((f) => {
    const m = marcas.get(f.key);
    if (!m || !conMarcasPorPersona(f) || esFilaCasa(f)) return f;
    if (!opciones.reemplazar && (f.transporte || f.bodegueroFijo)) return f;
    const bodeguero = m.bodegueroFijo && !!f.esBodega;
    if (!!f.transporte === m.transporte && !!f.bodegueroFijo === bodeguero) return f;
    marcadas++;
    return { ...f, transporte: m.transporte, bodegueroFijo: bodeguero };
  });
  return { filas: out, copiadas, respetadas, saltadas, marcadas };
}

/** «Ana», «Ana y Beto», «Ana, Beto y Carla», «Ana, Beto, Carla y 4 más». */
export function listaDeNombres(nombres: string[], max = 3): string {
  if (!nombres.length) return '';
  if (nombres.length === 1) return nombres[0];
  if (nombres.length <= max) return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
  return `${nombres.slice(0, max).join(', ')} y ${nombres.length - max} más`;
}

/**
 * «Para enviar faltan 2 colaboradores sin meta ni motivo» (escritorio) o
 * «Faltan 2 colaboradores sin meta ni motivo» (teléfono, `corto`).
 */
export function textoFaltan(n: number, corto = false): string {
  const cuerpo = n === 1 ? 'falta 1 colaborador sin meta ni motivo' : `faltan ${n} colaboradores sin meta ni motivo`;
  return corto ? cuerpo.charAt(0).toUpperCase() + cuerpo.slice(1) : `Para enviar ${cuerpo}`;
}

/**
 * Por qué NO se puede enviar, en palabras, o `null` si se puede.
 *
 * Se decide con lo que hay en pantalla (errores de celda y pendientes en vivo)
 * y con el estado: enviada y autorizada son de sólo lectura (no se reenvían).
 * Si hay cambios sin guardar, «Enviar» guarda primero. El servidor vuelve a
 * revisar todo al enviar.
 */
export function motivoNoEnviar(x: {
  puedeCapturar: boolean;
  /**
   * El porqué que da la API cuando no se puede capturar (`permisos.motivoNoCapturar`).
   * Una semana TERMINADA y autorizada no es «tu cuenta no puede»: la cuenta
   * sí puede, pero la tienda sólo se corrige si el regional la rechaza.
   */
  motivoNoCapturar?: string | null;
  semanaEditable: boolean;
  estado: EstadoTienda;
  cambiosSinGuardar: boolean;
  erroresCelda: number;
  pendientes: number;
  total: number;
  /**
   * Pendientes de catálogo que bloquean (`permisos.bloqueoCatalogo`, fase 4):
   * el porqué en palabras. Va al final: lo que el supervisor corrige aquí
   * (errores, personas sin meta ni motivo) se dice primero.
   */
  bloqueoCatalogo?: string | null;
}): string | null {
  if (!x.puedeCapturar) return x.motivoNoCapturar || 'Tu cuenta no puede capturar esta tienda.';
  if (!x.semanaEditable) return 'Esta semana ya no se puede modificar.';
  if (x.estado === 'enviada') return 'Ya está enviada a autorización: es de sólo lectura hasta que el regional la resuelva.';
  if (x.estado === 'autorizada') return 'Ya está autorizada: sólo un administrador puede reabrirla.';
  if (x.total === 0) return 'La tienda no tiene colaboradores en la plantilla.';
  if (x.erroresCelda > 0)
    return `Corrige ${x.erroresCelda === 1 ? 'el error marcado' : `los ${x.erroresCelda} errores marcados`} antes de enviar.`;
  if (x.pendientes > 0) return textoFaltan(x.pendientes);
  if (x.bloqueoCatalogo) return `No se puede enviar: ${x.bloqueoCatalogo}`;
  return null;
}

/**
 * Las tiendas del selector de la Captura: las que la API marca `verEnCaptura`
 * (la regla vive en la API desde la fase 2b; antes se repetía aquí con
 * `tiendasDeCaptura`/`capturaLaTienda` y se desviaba). Una API anterior que no
 * lo mande cae a `puedeCapturar`.
 */
export function tiendasDelSelector<T extends { puedeCapturar: boolean; verEnCaptura?: boolean }>(lista: {
  tiendas: T[];
}): T[] {
  return lista.tiendas.filter((t) => t.verEnCaptura ?? t.puedeCapturar);
}

// ------------------------------------------------------------ la Captura del supervisor (21-oct-2026)

/**
 * Los chips de la Captura para quien REVISA (diseño `Supervisor-revision`,
 * 21-oct-2026): cada tienda del selector va en un grupo según dónde está.
 * Con la captura de la tienda encendida (`capturaTienda`):
 * - `te_la_enviaron`: el gerente se la mandó (`revision`): le toca a él.
 * - `rechazadas`: el regional se la rechazó: también le toca a él.
 * - `en_gerente`: sin captura o en borrador (del gerente o suya).
 * - `devueltas`: borradores que él le devolvió al gerente y siguen sin volver.
 * - `enviadas`: ya salieron al regional (enviadas y autorizadas).
 * Apagada (por omisión; 22-oct-2026, decisión del usuario: «de momento seguir
 * dejando la tarea de capturar la meta a los supervisores»), los mismos chips
 * SIN el gerente: `en_captura` junta sin captura, borrador y lo que quedara
 * «con el supervisor» o devuelto de cuando estaba encendida (lo sigue
 * capturando él).
 */
export type FiltroCaptura = 'te_la_enviaron' | 'rechazadas' | 'en_gerente' | 'devueltas' | 'en_captura' | 'enviadas' | 'todas';

export const FILTROS_CAPTURA: { id: FiltroCaptura; etiqueta: string }[] = [
  { id: 'te_la_enviaron', etiqueta: 'Te la enviaron' },
  { id: 'rechazadas', etiqueta: 'Rechazadas por el regional' },
  { id: 'en_gerente', etiqueta: 'En captura del gerente' },
  { id: 'devueltas', etiqueta: 'Devueltas' },
  { id: 'enviadas', etiqueta: 'Enviadas al regional' },
  { id: 'todas', etiqueta: 'Todas' },
];

/** Sin la captura de la tienda (22-oct-2026): nada del gerente. */
export const FILTROS_CAPTURA_SUPERVISOR: { id: FiltroCaptura; etiqueta: string }[] = [
  { id: 'rechazadas', etiqueta: 'Rechazadas por el regional' },
  { id: 'en_captura', etiqueta: 'En captura' },
  { id: 'enviadas', etiqueta: 'Enviadas al regional' },
  { id: 'todas', etiqueta: 'Todas' },
];

/** Los chips según el interruptor del entorno (`capturaDeTienda`); por omisión, sin la tienda. */
export const filtrosCaptura = (capturaTienda = false): { id: FiltroCaptura; etiqueta: string }[] =>
  capturaTienda ? FILTROS_CAPTURA : FILTROS_CAPTURA_SUPERVISOR;

export function grupoCaptura(t: Pick<TiendaSemana, 'estado' | 'devuelta'>, capturaTienda = false): Exclude<FiltroCaptura, 'todas'> {
  switch (t.estado) {
    case 'revision':
      return capturaTienda ? 'te_la_enviaron' : 'en_captura';
    case 'rechazada':
      return 'rechazadas';
    case 'enviada':
    case 'autorizada':
      return 'enviadas';
    case 'borrador':
      if (!capturaTienda) return 'en_captura';
      return t.devuelta ? 'devueltas' : 'en_gerente';
    default:
      return capturaTienda ? 'en_gerente' : 'en_captura';
  }
}

export const coincideFiltroCaptura = (
  t: Pick<TiendaSemana, 'estado' | 'devuelta'>,
  f: FiltroCaptura,
  capturaTienda = false,
): boolean => f === 'todas' || grupoCaptura(t, capturaTienda) === f;

export function conteosCaptura(tiendas: Pick<TiendaSemana, 'estado' | 'devuelta'>[], capturaTienda = false): Record<FiltroCaptura, number> {
  const c: Record<FiltroCaptura, number> = {
    te_la_enviaron: 0,
    rechazadas: 0,
    en_gerente: 0,
    devueltas: 0,
    en_captura: 0,
    enviadas: 0,
    todas: 0,
  };
  for (const t of tiendas) {
    c.todas++;
    c[grupoCaptura(t, capturaTienda)]++;
  }
  return c;
}
